import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "Add Client" dialog — standalone CRM client creation. Does NOT require a
 * booking. Re-uses an existing client if the same (provider, phone) already
 * exists. The dialog returns the new/existing client via `onCreated`.
 */
export default function AddClientDialog({ onCreated, triggerVariant = 'default', triggerLabel = 'Add Client' }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');

  const reset = () => {
    setName(''); setPhone(''); setEmail(''); setAddress(''); setNotes('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error('Name and phone are required');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/dashboard/clients`, {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || null,
        address: address.trim() || null,
        notes: notes.trim() || null,
      }, { headers });
      if (data.created) {
        toast.success('Client added');
      } else {
        toast.success('Client already exists — using existing record');
      }
      reset();
      setOpen(false);
      onCreated?.(data.client);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={triggerVariant} data-testid="add-client-btn">
          <UserPlus className="h-4 w-4 mr-1" /> {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="add-client-dialog">
        <DialogHeader>
          <DialogTitle>Add Client</DialogTitle>
          <DialogDescription>
            Save a client to your CRM. You can book them later, or add their animals first.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="client-name">Name *</Label>
            <Input id="client-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Jane Rancher" data-testid="add-client-name" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="client-phone">Phone *</Label>
            <Input id="client-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567" data-testid="add-client-phone" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="client-email">Email <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Input id="client-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@example.com" data-testid="add-client-email" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="client-address">Address <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Input id="client-address" value={address} onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Pasture Rd, Austin, TX" data-testid="add-client-address" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="client-notes">Notes <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Textarea id="client-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything you'd like to remember about this client..." data-testid="add-client-notes" />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-client-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save client
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
